import OptionGroup from "lib/hex/views/components/OptionGroup";
import { registerForm } from "lib/hex/views/formRegistry";
import PropTypes from "prop-types";
import React from "react";
import Select from "lib/common/controls/Select";
import DateRange, {
    dateToString,
    isDateRangeValid,
} from "lib/hex/views/components/DateRange";
import useKeyValueState from "lib/hex/views/components/useKeyValueState";
import {
    fromCommaSafeArray,
    stringifyJson,
    toCommaSafeArray,
    unstringifyJson,
} from "lib/hex/views/components/serializeParams";

const filterVersions = (options, val, greater = false) => {
    // convert versions into comparable values
    let found = false;
    return options.filter( it => {
        if(val === it.value) {
            found = true;
            return true;
        }
        return greater ? found : !found;
    });
};

const DataVersionSelect = ({name, onChange, value = [], options, disabled=false}) => {
    const hasFromSelected = value.length >= 1;
    const toOptions = filterVersions(options, value[0], true);
    const handleChange = (key, v) => {
        if(key === "to") {
            // exporter expects a list of datavrsns within the selected range.
            const from = value[0];
            const to = v || value[0];
            let inRange = false;
            v = [];
            toOptions.forEach(it => {
                if(it.value === from) {
                    inRange = true;
                }
                if(inRange) {
                    v.push(it.value);
                }
                if(it.value === to) {
                    inRange = false;
                }
            });
        }
        else {
            v = [v];
        }
        onChange(v.filter(it => it !== ""));
    };
    return (
        <div className="form-group">
            <div className="col-sm-6">
                From
                <Select
                    name={name + "-from"}
                    className="dataversions-from form-control"
                    onCommitChange={(v) => handleChange("from", v)}
                    value={value[0]}
                    options={options}
                    disabled={disabled}
                />
            </div>
            <div className="col-sm-6">
                To
                <Select
                    name={name + "-to"}
                    className="dataversions-to form-control"
                    onCommitChange={(v) => handleChange("to", v)}
                    value={value[value.length-1]}
                    options={toOptions}
                    disabled={disabled || !hasFromSelected}
                />
            </div>
        </div>
    );
};

DataVersionSelect.propTypes = {
    name: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.array,
    options: PropTypes.array,
    disabled: PropTypes.bool,
};

const FormView = ({onChange, state, data}) => (
    <div>
        <div className="form-section form-horizontal">
            <div className="form-section-inner">
                <h3>Participant ID</h3>
                <div className="form-group">
                    <div className="col-sm-12">
                        <Select
                            name="particids"
                            className="participant form-control"
                            onCommitChange={(v) => onChange("particids", v)}
                            onBlur={(v) => onChange("particidsBlur", state.particids)}
                            value={state.particids}
                            options={data.particids}
                            multiple={true}
                            required
                        />
                    </div>
                </div>
                <h5>Data Versions</h5>
                <DataVersionSelect
                    name="datavrsns"
                    onChange={(v) => onChange("datavrsns", v)}
                    value={state.datavrsns}
                    options={data.datavrsns}
                    disabled={state.particids.length==0}
                />
                <DateRange
                    start={state.start}
                    end={state.end}
                    min={data.daterange.from}
                    max={data.daterange.to}
                    onChange={onChange}
                    disabled={state.particids.length==0}
                    required
                />
                <h5>Surgeons</h5>
                <div className="form-group">
                    <div className="col-sm-12">
                        <Select
                            name="surgeons"
                            className="surgeons form-control"
                            onCommitChange={(v) => onChange("surgeons", v)}
                            value={state.surgeons}
                            multiple={true}
                            options={data.surgeons}
                            disabled={state.particids.length==0 || !isDateRangeValid(
                                state.start,
                                state.end,
                            )}
                        />
                    </div>
                </div>
                <h5>Procedure Type</h5>
                <div className="form-group">
                    <div className="col-sm-12">
                        <Select
                            name="procedureTypes"
                            className="procedureTypes form-control"
                            onCommitChange={(v) => onChange("procedureTypes", v)}
                            value={state.procedureTypes}
                            multiple={true}
                            options={data.procedureTypes}
                            disabled={state.particids.length==0}
                        />
                    </div>
                </div>
            </div>
        </div>
        <div className="form-section form-horizontal">
            <div className="form-section-inner">
                <h3>Export Options</h3>
                <div className="form-group">
                    <label htmlFor="" className="col-md-5 control-label">Field
                        Name Format</label>
                    <div className="col-md-7">
                        <Select
                            name="fieldnameFormat"
                            className="fieldnameFormat format_select form-control"
                            onCommitChange={(v) => onChange("fieldnameFormat", v)}
                            value={state.fieldnameFormat}
                            options={data.fieldnameFormat}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor=""
                           className="col-md-5 control-label">Delimiter</label>
                    <div className="col-md-7">
                        <Select
                            name="delimiter"
                            className="delimiter format_select form-control"
                            onCommitChange={(v) => onChange("delimiter", v)}
                            value={state.delimiter}
                            options={data.delimiter}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="" className="col-md-5 control-label">Choice
                        Format</label>
                    <div className="col-md-7">
                        <Select
                            name="optionFormat"
                            className="optionFormat format_select form-control"
                            onCommitChange={(v) => onChange("optionFormat", v)}
                            value={state.optionFormat}
                            options={data.optionFormat}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="" className="col-md-5 control-label">Date
                        Format</label>
                    <div className="col-md-7">
                        <Select
                            name="dateFormat"
                            className="dateFormat format_select form-control"
                            onCommitChange={(v) => onChange("dateFormat", v)}
                            value={state.dateFormat}
                            options={data.dateFormat}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="" className="col-md-5 control-label">Field
                        Sort</label>
                    <div className="col-md-7">
                        <Select
                            name="fieldSort"
                            className="dateFormat format_select form-control"
                            onCommitChange={(v) => onChange("fieldSort", v)}
                            value={state.fieldSort}
                            options={data.fieldSort}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="" className="col-md-5 control-label">File
                    Format</label>
                    <div className="col-md-7">
                        <Select
                            name="fileFormat"
                            className="dateFormat format_select form-control"
                            onCommitChange={(v) => onChange("fileFormat", v)}
                            value={state.fileFormat}
                            options={data.fileFormat}
                        />
                    </div>
                </div>
            </div>
            <OptionGroup
                state={state.exportFields}
                data={data.exportFields}
                expanded={false}
                onChange={(value) => onChange("exportFields", value)}
                hasFieldEntity={true}
            />
        </div>
    </div>
);

const selectShape = PropTypes.arrayOf(
    PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
    })
);

FormView.propTypes = {
    onChange: PropTypes.func.isRequired,
    state: PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string,
        particids: PropTypes.array,
        fieldnameFormat: PropTypes.string,
        delimiter: PropTypes.string,
        optionFormat: PropTypes.string,
        dateFormat: PropTypes.string,
        fieldSort: PropTypes.string,
        datavrsns: PropTypes.array,
        surgeons: PropTypes.array,
        procedureTypes: PropTypes.array,
        exportFields: PropTypes.object,
        fileFormat: PropTypes.string,
        particidsBlur: PropTypes.array,
        activeMembers: PropTypes.array,
    }),
    data: PropTypes.shape({
        fieldnameFormat: selectShape,
        delimiter: selectShape,
        optionFormat: selectShape,
        dateFormat: selectShape,
        fieldSort: selectShape,
        particids: selectShape,
        datavrsns: selectShape,
        surgeons: selectShape,
        procedureTypes: selectShape,
        daterange: PropTypes.shape({
            from: PropTypes.array,
            to: PropTypes.array
        }),
        exportFields: PropTypes.array,
        fileFormat: selectShape,
        activeMembers: selectShape,
    }),
};

const initialStateWithDefaults = (state = {}) => {
    const newState = {
        start: dateToString(state.start || ""),
        end: dateToString(state.end || ""),
        particids: state.particids || [],
        fieldnameFormat: state.fieldnameFormat || "SHORT",
        delimiter: state.delimiter || "|",
        optionFormat: state.optionFormat || "NAME",
        dateFormat: state.dateFormat || "MM/dd/yyyy",
        fieldSort: state.fieldSort || "FIELD",
        datavrsns: state.datavrsns || [],
        surgeons: state.surgeons || [],
        procedureTypes: state.procedureTypes || [],
        exportFields: state.exportFields || {},
        fileFormat: state.fileFormat || "SINGLE_FILE",
        particidsBlur: state.particidsBlur || [],
    };
    return unstringifyJson(
        fromCommaSafeArray(newState, "surgeons", "procedureTypes"),
        "exportFields",
    );
};

const serverDataParams = params =>
    stringifyJson(
        toCommaSafeArray(params, "surgeons", "procedureTypes"),
        "exportFields",
    );

const initialData = {
    fieldnameFormat: [
        "SHORT",
        "LONG"
    ].map(v => ({label: v, value: v})),
    delimiter: [
        {value: "|", label: "|"},
        {value: "&#09;", label: "TAB"},
        {value: ",", label: ","},
    ],
    optionFormat: [
        "NAME",
        "HARVEST_VALUE",
    ].map(v => ({label: v, value: v})),
    fileFormat: [
        {
            label: "Single File",
            value: "SINGLE_FILE"
        },
        {
            label: "File Per-Participant",
            value: "FILE_PER_PARTICIPANT"
        }
    ],
    dateFormat: [
        "MM/dd/yyyy",
        "yyyy-MM-dd",
        "dd/MM/yyyy",
    ].map(v => ({label: v, value: v})),
    fieldSort: [
        {
            label: "By Field Name",
            value: "FIELD"
        },
        {
            label: "By Section then Field Name",
            value: "SECTIONFIELD"
        }
    ],
    particids: [],
    datavrsns: [],
    surgeons: [],
    procedureTypes: [],
    daterange: {from: [], to: []},
    exportFields: [],
};

const AdultCardiacExportForm = ({initialState, needServerData, onReady, onNotReady, activeMembers}) => {
    const [ state, setState ] = React.useState(() =>
        initialStateWithDefaults(initialState));
    React.useEffect(
        () => setState(initialStateWithDefaults(initialState)),
        [ initialState ],
    );
    const [ data, setDataKey ] = useKeyValueState(initialData);

    React.useEffect(() => {
        needServerData({
            endpoint: "/exportable/hospitals",
            loadingMessage: "Loading Participants...",
            onData: (data) => {
                data = data.map(it => ({
                    label: `${it.particid}: ${it.hospname}`,
                    value: it.particid,
                }));
                data.unshift({label: "All Hospitals", value: ""});
                setDataKey("particids", data);
            },
        });
    }, [activeMembers]);

    React.useEffect(() => {
        needServerData({
            endpoint: "/exportable/fields",
            loadingMessage: "Loading Available Fields...",
            onData: (data) => {
                const fields = data;
                if(_.isEmpty(state.exportFields)) {
                    // initially all fields are selected if none were provided.
                    const selected = {};
                    fields.forEach(it => {
                        it.fields.forEach(f => {
                            if(!selected[f.entity]) {
                                selected[f.entity] = [];
                            }
                            selected[f.entity].push(f.key);
                        });
                    });
                    handleChange("exportFields", selected);
                }
                setDataKey("exportFields", fields);
            }
        });
    }, []);

    React.useEffect(() => {
        const { particids, particidsBlur } = state;
        if (!particids || particids.length == 0) return;
        needServerData({
            endpoint: "/exportable/datavrsns",
            loadingMessage: "Loading Available Data Versions...",
            params: serverDataParams({
                particids,
            }),
            onData: (data) => {
                data = data.map(it => ({label: it.name, value: it.name}));
                data.reverse();
                data.unshift({label: "", value: ""});
                setDataKey("datavrsns", data);
            }
        });
    }, [state.particidsBlur]);

    React.useEffect(() => {
        const { particids, datavrsns, start, end, particidsBlur} = state;
        if (!particids || particids.length == 0 || !isDateRangeValid(state.start, state.end)) return;
        needServerData({
            endpoint: "/exportable/surgeons",
            loadingMessage: "Loading Available Surgeons...",
            params: serverDataParams({
                particids,
                datavrsns,
                surgdtStart: start,
                surgdtEnd: end
            }),
            onData: (data) => {
                data = data.map(it => ({
                    label: it.npi ? `${it.npi}: ${it.name}` : it.name,
                    value: it.name
                }));
                data.unshift({label: "All Surgeons", value: ""});
                setDataKey("surgeons", data);
            }
        });
    }, [state.particidsBlur, state.datavrsns, state.start, state.end]);

    React.useEffect(() => {
        needServerData({
            endpoint: "/exportable/procedure-types",
            loadingMessage: "Loading Procedure Types...",
            onData: (data) => {
                data = data.map(it => ({
                    label: it.name,
                    value: it.name,
                }));
                data.unshift({label: "All Procedure Types", value: ""});
                setDataKey("procedureTypes", data);
            },
        });
    }, []);

    React.useEffect(() => {
        const { particids, datavrsns, particidsBlur } = state;
        if (!particids || particids.length < 1) return;
        needServerData({
            endpoint: "/exportable/surgdt-range",
            loadingMessage: "Loading Available Date Range...",
            params: serverDataParams({
                particids,
                datavrsns,
            }),
            onData: (data) =>
                setDataKey("daterange", {from: data.from, to: data.to}),
        });
    }, [state.particidsBlur, state.datavrsns]);

    React.useEffect(() => {
        // update the submit button and data
        if (isDateRangeValid(state.start, state.end) && !!state.particids) {
            const availableSurgeons = new Set(data.surgeons.map(it => it.value));
            onReady(serverDataParams({
                ...state,
                surgeons: state.surgeons
                    .filter(it => availableSurgeons.has(it)),
                exportFields: Object.entries(state.exportFields)
                    .reduce((agg, [ e, fs ]) => {
                        if (fs && fs.length) agg[e] = fs;
                        return agg;
                    }, {}),
            }));
        } else {
            onNotReady();
        }
    }, [ state, data ]);

    const handleChange = React.useCallback(
        (key, value) => setState(s => ({ ...s, [key]: value })),
        [ setState ],
    );

    return (
        <FormView
            state={state}
            data={data}
            onChange={handleChange}
        />
    );
};

AdultCardiacExportForm.propTypes = {
    initialState: PropTypes.shape({
        start: PropTypes.any,
        end: PropTypes.any,
        particids: PropTypes.arrayOf(PropTypes.string),
        fieldnameFormat: PropTypes.string,
        delimiter: PropTypes.string,
        optionFormat: PropTypes.string,
        dateFormat: PropTypes.string,
        fieldSort: PropTypes.string,
        datavrsns: PropTypes.arrayOf(PropTypes.string),
        surgeons: PropTypes.arrayOf(PropTypes.string),
        procedureTypes: PropTypes.arrayOf(PropTypes.string),
        // history stores an object, saved params have JSON strings
        exportFields: PropTypes.oneOfType([ PropTypes.object, PropTypes.string ]),
        fileFormat: PropTypes.string
    }),
    needServerData: PropTypes.func.isRequired,
    onReady: PropTypes.func.isRequired,
    onNotReady: PropTypes.func.isRequired,
};

// AdultCardiacExportForm.canRerun = params =>
//     params.hasOwnProperty("fieldnameFormat") &&
//     params.hasOwnProperty("delimiter") &&
//     params.hasOwnProperty("optionFormat") &&
//     params.hasOwnProperty("dateFormat") &&
//     params.hasOwnProperty("exportFields");

AdultCardiacExportForm.canRerun = params => !!(params);

AdultCardiacExportForm.apiPrefix = "adult-cardiac";

registerForm("adult-cardiac-export", AdultCardiacExportForm);




// WEBPACK FOOTER //
// ./export_forms/api-sts-adult-cardiac-2025.10.07-jar/hybrid-jsclient/AdultCardiacExportForm.js